import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings → Easeedesk → Adam Intop: setarile generale, scenariile pe tip de
 * tichet, si cele doua ecrane mici de intretinere a actiunilor.
 *
 * Perechile vechi: `settings/easeedesk/adam_intop_settings`,
 * `get_users_by_group`, `getCompaniesAI`, `save_adam_intop_settings`,
 * `adam_intop_scenarios`, `save_adam_intop_scenarios_settings`,
 * `clean_removed_actions`, `clean_removed_actions_save`,
 * `assign_company_actions`, `assign_company_actions_save`.
 *
 * `apiGet`/`apiPost` pun singure `/api/v2` in fata caii.
 */

const BASE = '/settings/easeedesk/adam_intop'

export interface NamedId {
  ID: number
  name: string
}

export interface AdamUser {
  ID: number
  user_name: string
}

export interface AdamTicketType {
  ID: number
  type_name: string
  short_name: string
}

export interface AdamStatus {
  ID: number
  status_name: string
}

export interface AdamPriority {
  ID: number
  priority_name: string
}

export interface AdamStatuses {
  default: AdamStatus[]
  custom: AdamStatus[]
}

/** Cele 21 de campuri ale ecranului, ca perechi nume-valoare - ca in tabela. */
export type AdamSettingsValues = Record<string, string>

export interface AdamSettingsShell {
  title: string
  moduleUrl: string
  values: AdamSettingsValues
  assigned_companies: number[]
  departments: NamedId[]
  users: AdamUser[]
  ticket_types: AdamTicketType[]
  statuses: AdamStatuses
  checked_ticket_types: number[]
}

export interface AdamCompany {
  ID: number
  name: string
  checked: number
}

export interface AdamScenarioRow {
  [field: string]: string | number | null
}

export interface AdamScenariosShell {
  title: string
  moduleUrl: string
  ticket_types: AdamTicketType[]
  ticket_types_allowed: AdamTicketType[]
  statuses: AdamStatuses
  priorities: AdamPriority[]
  departments: NamedId[]
  scenarios: Record<string, AdamScenarioRow>
}

export function adamSettingsQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'settings'],
    queryFn: () => apiGet<AdamSettingsShell>(`${BASE}/settings`),
  })
}

/** `get_users_by_group`: oamenii unui grup, cand se schimba grupul. */
export function adamUsersQuery(group: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'settings', 'users', group],
    queryFn: () => apiGet<{ users: AdamUser[] }>(`${BASE}/settings/users/${group}`),
    enabled: group > 0,
  })
}

/** `getCompaniesAI`: firmele furnizorului, cu bifa alaturi. */
export function adamCompaniesQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'companies'],
    queryFn: () => apiGet<{ companies: AdamCompany[] }>(`${BASE}/companies`),
  })
}

/** `save_adam_intop_settings`. */
export function saveAdamSettings(values: AdamSettingsValues, ticketTypes: number[], companies: number[]) {
  const fields: Record<string, PostValue> = { ...values }
  fields['run_only_on_ticket_types'] = ticketTypes.join(',')
  fields['assigned_companies[]'] = companies
  return apiPost<{ ok?: boolean }>(`${BASE}/settings`, fields)
}

export function adamScenariosQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'scenarios'],
    queryFn: () => apiGet<AdamScenariosShell>(`${BASE}/scenarios`),
  })
}

/**
 * `save_adam_intop_scenarios_settings`: cate un rand pe tip de tichet, sub
 * `scenario[<tip>][<camp>]`, exact cum le trimitea formularul vechi.
 */
export function saveAdamScenarios(scenarios: Record<number, Record<string, string>>) {
  const fields: Record<string, PostValue> = {}
  for (const [type, row] of Object.entries(scenarios)) {
    for (const [field, value] of Object.entries(row)) {
      fields[`scenario[${type}][${field}]`] = value
    }
  }
  return apiPost<{ ok?: boolean }>(`${BASE}/scenarios`, fields)
}

/** `clean_removed_actions`: o pagina cu un buton. */
export function adamCleanQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'clean_removed_actions'],
    queryFn: () => apiGet<{ title: string; moduleUrl: string }>(`${BASE}/clean_removed_actions`),
  })
}

/** `clean_removed_actions_save`. */
export function cleanRemovedActions() {
  return apiPost<{ ok?: boolean; removed?: number; message?: string }>(`${BASE}/clean_removed_actions`, {})
}

/** `assign_company_actions`. */
export function adamAssignQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'assign_company_actions'],
    queryFn: () => apiGet<{ title: string; moduleUrl: string; companies: NamedId[] }>(`${BASE}/assign_company_actions`),
  })
}

/** `assign_company_actions_save`. */
export function assignCompanyActions(companyId: number) {
  return apiPost<{ ok?: boolean; message?: string }>(`${BASE}/assign_company_actions`, { companyId })
}

/** `adam_intop_cron_stucked_tickets`: doar numele furnizorului, pentru titlu. */
export function adamCronShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'cron'],
    queryFn: () => apiGet<{ providerName: string }>('/settings/easeedesk/adam_intop/cron'),
  })
}

/** `rebuild_adam_intop_cron_stuck_commands` - butonul "Refresh cron". */
export function rebuildAdamStuckCron() {
  /* `message` vine numai cand n-a mers: de ce (gazda, cod, ce sa verifici). */
  return apiPost<{ result: 'done' | 'failed'; message?: string }>('/settings/easeedesk/adam_intop/cron/rebuild', {})
}
